import type { Messages } from './types.ts';

export const en: Messages = {
  shell: {
    help: 'Help and support',
    helpWhatsapp: 'WhatsApp',
    helpWhatsappHint: 'Message our team',
    helpGuides: 'Video guides',
    helpGuidesHint: 'One guide per screen',
    helpCenter: 'Help centre',
    helpCenterHint: 'Articles and frequent questions',
    searching: 'Searching…',
    orderResult: '{{number}} · {{customer}}',
    ordersFound: 'Orders',
    appName: 'Al-WidadiaShop',
    search: 'Search an order, a product…',
    searchPlaceholder: 'Order, product, customer, page…',
    commands: 'Quick commands',
    noResults: 'No results',
    userMenu: 'User menu',
    tenant: 'Store',
    badge_ordersToConfirm_one: '{{label}}, {{count}} order to confirm',
    badge_ordersToConfirm_other: '{{label}}, {{count}} orders to confirm',
    badge_missingParcels_one: '{{label}}, {{count}} missing parcel',
    badge_missingParcels_other: '{{label}}, {{count}} missing parcels',
    switchTenant: 'Switch store',
    switchTenantConfirm: 'Switch',
    logout: 'Sign out',
    login: 'Sign in',
    loggingIn: 'Redirecting to sign in…',
    checkingSession: 'Checking your session…',
    openingWorkspace: 'Preparing your workspace…',
    stepSession: 'Session',
    stepSignIn: 'Sign-in',
    stepWorkspace: 'Workspace',
    secureSignIn: 'Secure single sign-on',
    menu: 'Menu',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    loading: 'Loading',
    loginFailedTitle: 'Cannot sign in',
    callbackFailed: 'The session could not be completed. Go back to the home page and sign in again.',
    loginFailed: 'Sign-in failed. Check that Keycloak is running and try again.',
    signInTitle: 'Welcome back',
    signInDescription: 'Sign in with your Al-WidadiaShop account to open your workspace.',
    sessionExpiredTitle: 'Your session has expired',
    sessionExpiredDescription:
      'For your security you were signed out after a period of inactivity. Sign in again to continue where you left off.',
    insecureContext: 'Sign-in needs a secure context. Open the app over HTTPS or from a loopback origin.',
    forbidden: 'You do not have access to this page.',
    backHome: 'Back to dashboard',
    language: 'Language',
    theme: 'Theme',
    theme_light: 'Light',
    theme_dark: 'Dark',
    toggleTheme: 'Switch to the {{theme}} theme',
    theme_system: 'System',
    density: 'Density',
    density_comfortable: 'Comfortable',
    density_compact: 'Compact',
    density_spacious: 'Spacious',
    densityHint_compact: 'More on screen',
    densityHint_comfortable: 'Balanced',
    densityHint_spacious: 'More room, easier to read',
  },
  nav: {
    statistics: 'Statistics',
    statistics_delivery: 'Delivery',
    statistics_confirmation: 'Confirmation',
    statistics_products: 'Products',
    statistics_orders: 'Orders',
    statistics_marketing: 'Marketing',
    calculator: 'Calculator',
    labels: 'Labels',
    financeOverview: 'Overview',
    remittances: 'Carrier statements',
    disputes: 'Disputes',
    expenses: 'Expenses',
    handoverShort: 'Handover',
    group_sales: 'Sales',
    group_logistics: 'Logistics',
    group_catalog: 'Catalog',
    handover: 'Carrier handover',
    returns: 'Returns',
    returnedParcels: 'Returned',
    missingParcels: 'Missing',
    finance: 'Finance',
    dashboard: 'Dashboard',
    orders: 'Orders',
    products: 'Products',
    settings: 'Settings',
    categories: 'Categories',
    inventory: 'Inventory',
    tenants: 'Stores',
    customers: 'Customers',
    history: 'Change history',
    newOrder: 'New order',
  },
  dashboard: {
    filters: {
      product: 'Product',
      allProducts: 'All products',
      findProduct: 'Find a product (name or reference)',
      noProductMatch: 'No product found',
      origin: 'Origin',
      allOrigins: 'All origins',
      allImported: 'Imported (all countries)',
      shopWideHidden:
        'Charges and net profit cover the whole shop, so they are hidden while a product or an origin is chosen.',
    },
    greeting: 'Hello {{name}}',
    openOrders: 'View orders',
    newOrder: 'New order',
    newProduct: 'Add a product',
    points: '{{value}} pts',
    deltaLabel: '{{value}} compared with the same stretch just before',
    noOrdersTitle: 'No orders in this period',
    noOrdersDescription: 'Sales, deliveries and returns will show here from the first order.',
    seeInventory: 'Open stock',
    urgentNoneHelp: 'No item is below its low-stock threshold.',
    urgentNone: 'Nothing to restock',
    urgentOut: 'Out of stock',
    urgentThreshold: 'threshold {{count}}',
    urgentLeft: '{{count}} left',
    urgentTitle: 'To restock',
    noDepotsHelp: 'Create a depot in Stock to follow your quantities.',
    noDepots: 'No active depot',
    valueMissing: 'Items in stock without a cost: {{count}}. The value is understated.',
    value: 'Value',
    out: 'Out of stock',
    low: 'Low stock',
    reserved: 'Reserved',
    available: 'Available',
    inStock: 'Items in stock',
    depot: 'Depot',
    depotsTitle: 'Stock per depot',
    stockValue: 'Stock value (at cost)',
    outOfStock: 'Items out of stock',
    toRestock: 'To restock',
    availableUnits: 'Units available',
    stockContext: 'What is in stock right now, depot by depot, and what to buy again.',
    title: 'Dashboard',
    context: 'Shop activity, result and stock',
    activeProducts: 'Active products',
    pendingMovements: 'Pending movements',
    emptyTitle: 'No products yet',
    emptyDescription: 'Create a first product to see these figures fill in.',
    performanceTitle: 'Activity in the period',
    created: 'Orders created',
    delivered: 'Orders delivered',
    deliveryRate: 'Delivery rate',
    returned: 'Returns',
    canceled: 'Canceled',
    sales: 'Delivered sales',
    grossMargin: 'Gross margin',
    net: 'Net result',
    recentTitle: 'Orders created',
    recentLabel_one: 'Orders created on the last day',
    recentLabel_other: 'Orders created per day over the last {{count}} days',
    recentDays_one: '{{count}} d',
    recentDays_other: '{{count}} d',
    parcelsTitle: 'Parcels on the way',
    parcelsTotal_one: '{{total}} parcel with the carriers',
    parcelsTotal_other: '{{total}} parcels with the carriers',
    parcelsNone: 'No parcel with the carriers',
    parcelsNoneHelp: 'Dispatched parcels show here until they are delivered or returned.',
    awaitingConfirmation_one: '{{total}} order is waiting for confirmation',
    awaitingConfirmation_other: '{{total}} orders are waiting for confirmation',
    seriesTitle: 'Trend',
    chartLabel: '{{metric}} per period',
    resultTitle: 'Income statement',
    costOfGoods: 'Cost of goods sold',
    carrierFees: 'Carrier fees',
    returnFees: 'Return fees',
    damageLoss: 'Losses on damaged goods',
    expenses: 'Expenses',
    returnedValue: 'Value of returns',
    missingCosts:
      'Lines sold without a cost: {{count}}. The margin is overstated until product costs are filled in.',
    costHidden: 'Margin and net result are reserved to people who can see costs.',
    topTitle: 'Best sellers',
    product: 'Product',
    sku: 'SKU',
    units: 'Units',
    margin: 'Margin',
    noSales: 'No delivered sale in the period',
    noSalesHelp: 'Products show up here as soon as an order is delivered.',
    stockTitle: 'Catalog and stock',
    overview: {
      stockUnitsAt_one: '{{total}} unit · {{price}} each',
      stockUnitsAt_other: '{{total}} units · {{price}} each',
      stockPurchaseValue: 'Stock at purchase price',
      stockSaleValue: 'Stock at selling price',
      stockUnits_one: '{{total}} unit in stock',
      stockUnits_other: '{{total}} units in stock',
      from: 'From',
      to: 'To',
      products: 'Products',
      depots: 'Depots',
      charges: 'Expenses',
      totalGains: 'Total gains',
      totalReturns: 'Total returns',
      defectiveProducts: 'Defective products',
      netProfit: 'Net profit',
      totalSales: 'Orders received',
      deliveredSales: 'Delivered sales',
      netSalesGain: 'Net gain on sales',
      pendingSales: 'Pending sales',
      salesCount_one: '{{total}} received',
      salesCount_other: '{{total}} received',
      canceledOrders_one: '{{total}} canceled order',
      canceledOrders_other: '{{total}} canceled orders',
      deliveredCount_one: '{{total}} delivered',
      deliveredCount_other: '{{total}} delivered',
      pendingCount_one: '{{total}} pending',
      pendingCount_other: '{{total}} pending',
      deliveredOrders_one: '{{total}} delivered order',
      deliveredOrders_other: '{{total}} delivered orders',
      returnedOrders_one: '{{total}} returned order',
      returnedOrders_other: '{{total}} returned orders',
      damagedUnits_one: '{{total}} damaged unit',
      damagedUnits_other: '{{total}} damaged units',
      soldUnits_one: '{{total}} unit sold',
      soldUnits_other: '{{total}} units sold',
      topProductsTitle: 'Top products',
      customize: 'Customize',
      groupLabel: 'Grouping',
      group: {
        auto: 'Automatic',
        day: 'By day',
        week: 'By week',
        month: 'By month',
      },
      rankLabel: 'Ranking',
      rank: {
        units: 'By units',
        sales: 'By amount',
      },
      rankTitle: {
        units: 'Units sold per product',
        sales: 'Amount sold per product',
      },
      sizeLabel: 'Number of products',
      size_one: 'Top {{count}}',
      size_other: 'Top {{count}}',
      unitsSold_one: '{{total}} unit',
      unitsSold_other: '{{total}} units',
    },
    widgets: {
      deltaLabel: '{{value}} compared with the comparison period',
      comparisonSeries: 'Comparison period',
      trendTitle: {
        sales: 'Gains over time',
        created: 'Sales over time',
        confirmed: 'Confirmed orders over time',
        delivered: 'Delivered sales over time',
        returned: 'Returns over time',
        canceled: 'Cancelled orders over time',
      },
      trendMeasure: {
        sales: 'Gains',
        created: 'Sales',
        confirmed: 'Confirmed orders',
        delivered: 'Delivered sales',
        returned: 'Returns',
        canceled: 'Cancelled orders',
      },
      breakdownTitle: {
        orders: 'Orders by {{dimension}}',
        delivered: 'Deliveries by {{dimension}}',
        returned: 'Returns by {{dimension}}',
        sales: 'Gains by {{dimension}}',
        deliveryRate: 'Delivery rate by {{dimension}}',
      },
      breakdownMeasure: {
        orders: 'Orders',
        delivered: 'Delivered',
        returned: 'Returned',
        sales: 'Gains',
        deliveryRate: 'Delivery rate',
      },
      dimension: {
        carrier: 'carrier',
        governorate: 'governorate',
        source: 'source',
        status: 'status',
      },
      noCarrier: 'No carrier yet',
      noGovernorate: 'No governorate',
      other: 'Others',
      versus: '{{value}} (vs {{previous}})',
      kind: {
        funnel: 'Order funnel',
      },
      funnelStep: {
        received: 'Received',
        confirmed: 'Confirmed',
        shipped: 'Handed to the carrier',
        delivered: 'Delivered',
      },
      funnelFirst: 'All orders created in the period',
      funnelShare: '{{ofFirst}} of all orders · {{fromPrevious}} of the previous step',
      funnelReturned_one: '{{total}} order came back',
      funnelReturned_other: '{{total}} orders came back',
      funnelCanceled_one: '{{total}} order canceled, left out of the next steps',
      funnelCanceled_other: '{{total}} orders canceled, left out of the next steps',
      field: {
        tile: 'Figure',
        measure: 'Measure',
        chart: 'Chart',
        dimension: 'Split by',
        title: 'Title',
        titleHelp: 'Leave empty to keep the suggested title.',
        size: 'Width',
      },
      chart: {
        line: 'Lines',
        area: 'Area',
        columns: 'Columns',
        stacked: 'Stacked bars',
        grouped: 'Side-by-side bars',
        barList: 'Ranked bars',
        donut: 'Donut',
      },
      size: {
        sm: 'Small (a quarter)',
        md: 'Medium (a third)',
        lg: 'Large (two thirds)',
        full: 'Full width',
      },
      settingsTitle: 'Widget settings',
      apply: 'Apply',
      preview: 'Preview',
      galleryTitle: 'Add a widget',
      gallerySubtitle: 'Pick a figure or a chart; you can change it afterwards.',
      galleryFull: 'This view already has 24 widgets. Remove one to add another.',
      group: {
        indicators: 'Key figures',
        trends: 'Trends',
        breakdowns: 'Breakdowns',
        orders: 'Orders and products',
      },
      preset: {
        kpi: {
          description: 'A key figure with its trend and the change against the comparison period.',
        },
        salesTrend: {
          title: 'Gains over time',
          description: 'Delivered sales as an area, with the comparison period dashed.',
        },
        ordersTrend: {
          title: 'Orders over time',
          description: 'Orders created, as a line.',
        },
        returnsTrend: {
          title: 'Returns per week',
          description: 'Returned orders, week by week.',
        },
        deliveryRateByCarrier: {
          title: 'Delivery rate by carrier',
          description: 'One line per carrier, week by week.',
        },
        ordersByCarrier: {
          title: 'Orders by carrier',
          description: 'Side-by-side bars per carrier.',
        },
        statusesByDay: {
          title: 'Order statuses by day',
          description: 'Where the orders of each day stand now, stacked.',
        },
        salesBySource: {
          title: 'Gains by source',
          description: 'Facebook, Instagram, phone… as a donut.',
        },
        ordersByGovernorate: {
          title: 'Orders by governorate',
          description: 'The governorates that order the most.',
        },
        funnel: {
          title: 'Order funnel',
          description: 'From received to delivered, with the loss at each step.',
        },
        topProducts: {
          title: 'Best sellers',
          description: 'The products sold the most, as ranked bars.',
        },
      },
      compareLabel: 'Compare with',
      compare: {
        none: 'No comparison',
        previous: 'Previous period',
        last_year: 'Same period last year',
      },
      drag: 'Drag {{title}} to move it',
      moveEarlier: 'Move {{title}} earlier',
      moveLater: 'Move {{title}} later',
      configure: 'Settings of {{title}}',
      remove: 'Remove {{title}}',
      editing: 'Customizing the dashboard',
      editHint: 'Drag widgets or use the arrows to reorder them, open their settings, then save.',
      add: 'Add a widget',
    },
    views: {
      options: 'View options',
      leaveTitle: 'Leave without saving?',
      leaveMessage: 'Your changes to the dashboard are not saved yet.',
      leave: 'Leave',
      stay: 'Stay and save',
      label: 'Dashboard view',
      standard: 'Standard view',
      defaultName: '{{name}} (default)',
      makeDefault: 'Open this view by default',
      rename: 'Rename the view',
      delete: 'Delete the view',
      saveAs: 'Save as a new view',
      saveAsTitle: 'Save as a new view',
      renameTitle: 'Rename the view',
      nameLabel: 'Name',
      nameRequired: 'Give the view a name.',
      deleteTitle: 'Delete this view?',
      deleteMessage: 'The view “{{name}}” will be deleted. The standard view stays available.',
      created: 'View saved',
      updated: 'View updated',
      deleted: 'View deleted',
      madeDefault: 'This view now opens by default',
      failed: 'The view could not be saved. Try again.',
    },
  },
  period: {
    legend: 'Period',
    today: 'Today',
    last_7_days: 'Last 7 days',
    this_month: 'This month',
    last_month: 'Last month',
    this_year: 'This year',
  },
  placeholder: { comingSoon: 'This screen arrives in the next milestone.' },
  common: {
    rowAction: '{{action}}: {{name}}',
    copyBarcode: 'Copy barcode {{code}}',
    barcodeCopied: 'Barcode copied',
    copyFailed: 'Could not copy: select the barcode and copy it by hand.',
    clearFilters: 'Clear filters',
    refineSearch: 'Showing {{shown}} of {{total}} results: refine the search.',
    shown: '{{shown}} of {{total}}',
    loadMore: 'Show more',
    loadingMore: 'Loading…',
    retry: 'Try again',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    close: 'Close',
    all: 'All',
  },
  catalog: {
    categoriesContext: 'Organise your products into aisles and sub-aisles.',
    productsContext: 'Your catalog and its variants: prices, stock per depot and publishing status.',
    name: 'Name',
    slug: 'Address',
    status: 'Status',
    status_all: 'All',
    status_active: 'Active',
    status_archived: 'Archived',
    status_draft: 'Inactive',
    position: 'Position',
    positionHelp: 'Display order, lowest first.',
    parent: 'Parent category',
    noParent: 'None (top level)',
    actions: 'Actions',
    delete: 'Delete',
    newCategory: 'New category',
    editCategory: 'Edit category',
    categorySaved: 'Category saved',
    categoryArchived: 'Category archived',
    noCategories: 'No categories',
    noCategoriesHelp: 'Create a first category to organise your products.',
    nameRequired: 'The name is required',
    loadFailed: 'Could not load',
    reference: 'Reference',
    variants: 'Variants',
    price: 'Price',
    search: 'Search',
    searchPlaceholder: 'Name, reference or SKU',
    searchSubmit: 'Run the search',
    category: 'Category',
    newProduct: 'New product',
    productSaved: 'Product saved',
    productArchived: 'Product archived',
    noProducts: 'No products',
    noProductsHelp: 'Create a first product to start selling.',
    sku: 'SKU',
  },
  origin: {
    local: 'Local',
    imported: 'Imported · {{country}}',
  },
  productEditor: {
    essentialsHint: 'The name, description and classification your customers see.',
    mediaHint: 'Sharp photos sell better; the first one is the cover.',
    pricingHint: 'Purchase cost, selling price and promotion.',
    variantsHint: 'Sizes, colours and the code of each variant.',
    stockHint: 'Quantities per depot and the alert threshold.',
    shippingHint: 'Weight and dimensions sent to the carrier.',
    moreHint: 'Warranty and extra information.',
    media: 'Media',
    mediaDrop: 'Drop photos here or click to choose them',
    mediaHelp: 'JPEG, PNG, WebP or AVIF, 10 MB at most. You can also paste an image.',
    mediaEmpty: 'No image yet. The first image added becomes the cover.',
    mediaGallery: 'Product images',
    mediaImage: 'Image {{position}}',
    mediaCover: 'Cover',
    mediaAlt: 'Image description',
    mediaEarlier: 'Move image {{position}} earlier',
    mediaLater: 'Move image {{position}} later',
    mediaRemove: 'Remove image {{position}}',
    mediaDismiss: 'Dismiss',
    mediaAdd: 'Add',
    mediaRefused: '{{name}} is not an accepted image (JPEG, PNG, WebP, AVIF).',
    mediaPhase_hashing: 'Preparing…',
    mediaPhase_uploading: 'Uploading…',
    mediaPhase_processing: 'Checking and optimising…',
    newTitle: 'New product',
    context: 'Everything about the product on one page',
    createdBy: 'Created by {{name}}',
    sections: 'Product sections',
    essentials: 'Essentials',
    pricing: 'Pricing',
    variants: 'Variants',
    stock: 'Stock',
    shipping: 'Shipping',
    more: 'More options',
    languages: 'Languages',
    translationMissing: 'to translate',
    description: 'Description',
    origin: 'Origin',
    originHelp: 'Local or imported, with the country',
    originCountry: 'Imported from',
    originAddCountry: 'Add an import country',
    originFindCountry: 'Find a country',
    originNoCountry: 'No country matches',
    originSeveral:
      'Each origin becomes a variant with its own stock. Prices stay the same unless you turn on “Different price per variant”.',
    originRemove: 'Remove {{origin}}',
    referenceHelp: 'Suggested from the sub-company and the category; you can change it. Unique in your shop.',
    noCategory: 'No category',
    subCategory: 'Sub-category',
    noSubCategory: 'No sub-category',
    sellingPrice: 'Price',
    cost: 'Cost',
    costHelp: 'Visible to authorised roles only',
    margin: 'Margin',
    promo: 'Put on sale',
    regularPrice: 'Regular price',
    regularPriceHelp: 'The price before the sale, shown crossed out next to the price',
    perVariantPrice: 'Different price per variant',
    perVariantPriceHelp:
      'Purchase and selling prices are entered on each variant (e.g. local or imported origin)',
    barcode: 'Barcode',
    stockHelp: 'Stock only changes through movements, so every change is traceable.',
    weight: 'Weight',
    unitKg: 'kg',
    unitCm: 'cm',
    lengthCm: 'Length',
    widthCm: 'Width',
    heightCm: 'Height',
    weightHelp: 'Applied to every variant',
    warranty: 'Warranty (months)',
    saveDraft: 'Save draft',
    publish: 'Activate',
    deactivate: 'Deactivate',
    archivedNotice: 'Archived product: it no longer appears in lists or orders.',
    restore: 'Restore to draft',
    undo: 'Undo',
    errorsTitle: 'Fix these before saving',
    fixField: 'Go to field',
    unsavedChanges: 'Some changes are not saved. If you leave, they will be lost.',
    leaveTitle: 'Leave the editor?',
    leave: 'Leave without saving',
    stay: 'Stay',
    notFound: 'This product could not be found.',
    backToProducts: 'Back to products',
    outcome_created: 'Product created',
    outcome_saved: 'Product saved',
    outcome_published: 'Product activated',
    outcome_deactivated: 'Product deactivated',
    hasOptions: 'This product has options (colour, size…)',
    hasOptionsHelp: 'Each combination of values becomes a variant with its own SKU and stock.',
    optionsHelp: 'Tick the values this product comes in. Each combination becomes a variant.',
    newOptionName: 'New option (e.g. Material)',
    createOption: 'Create option',
    newValueFor: 'New value for {{option}}',
    addValue: 'Add value',
    valuesChosen_one: '{{count}} selected',
    valuesChosen_other: '{{count}} selected',
    optionLimit: 'Up to 3 options per product',
    variantsOnSale: 'Variants on sale: {{count}}',
    singleVariant: 'Single variant',
    stockTotal: 'Total quantity',
    stockAddVariant: 'Add a variant',
    stockSplitVariant: 'Variant',
    stockSplitQuantity: 'Quantity',
    stockSplitRemove: 'Remove variant row {{position}}',
    stockSplitHelp_one: 'Shared equally between the {{count}} variant',
    stockSplitHelp_other: 'Shared equally between the {{count}} variants',
    stockAddDepot: 'Add a depot:',
    stockDepots: 'Depots of this product',
    stockDefault: 'default',
    stockDropDepot: 'Remove {{depot}}',
    stockGrandTotal_one: 'Total: {{total}} unit in {{depots}} depots',
    stockGrandTotal_other: 'Total: {{total}} units in {{depots}} depots',
    stockThreshold: 'Notification threshold',
    stockThresholdHelp: 'You are alerted when the available stock reaches this level.',
    stockThresholdPlaceholder: 'e.g. 5',
    skuHelp: 'Leave empty to use the suggested SKU',
    initialStockHelp:
      'Enter the starting stock of variants that have no movement yet. It is recorded with the product.',
    initialStockFailed: 'Product saved, but its initial stock was not:',
    autosave_saving: 'Saving…',
    autosave_saved: 'Draft saved',
    autosave_failed: 'Autosave failed',
    errors: {
      FINANCE_REMITTED_BEFORE_DELIVERY:
        'The carrier reported the payment before the delivery; it will be applied as soon as the delivery is confirmed.',
      nameRequired: 'The name is required in the shop’s default language.',
      priceInvalid: 'Enter a positive amount with at most 3 decimals.',
      regularPriceTooLow: 'The regular price must be above the price.',
      priceBelowCost: 'The price must be equal to or above the purchase price.',
      variantRequired: 'Add at least one variant.',
      referenceRequired: 'The reference is required.',
      weightInvalid: 'Enter a weight in kg with at most 3 decimals.',
      warrantyInvalid: 'Enter a whole number of months, from 1 to 600.',
      optionValuesRequired: 'Choose an option and at least one value.',
      quantityInvalid: 'Enter a whole number from 0 to 100,000.',
      stockRequired: 'Enter at least one unit in stock in a depot.',
      dimensionInvalid: 'Enter a size in centimetres, e.g. 12.5',
      splitMismatch: 'The variant quantities must add up to the total quantity of the depot.',
    },
  },
  categoryList: {
    detailPlacement: 'Placement',
    detailPlacementHint: 'Where this category sits in the shop.',
    detailTranslationsHint: 'The name and address shown in each language.',
    status_active: 'Active',
    status_archived: 'Archived',
    productsUnit_one: 'product',
    productsUnit_other: 'products',
    summaryRoots_one: '{{total}} aisle',
    summaryRoots_other: '{{total}} aisles',
    summaryChildren_one: '{{total}} sub-category',
    summaryChildren_other: '{{total}} sub-categories',
    summaryProducts_one: '{{total}} product',
    summaryProducts_other: '{{total}} products',
    summaryArchived_one: '{{total}} archived category',
    summaryArchived_other: '{{total}} archived categories',
    nameIn: 'Name in {{language}}',
    nameRequiredIn: 'Required: the store shows it by default.',
    nameFallback: 'Optional. Without a translation, the store shows the {{language}} name.',
    address: 'Address',
    addressPending: 'Created when saved',
    addressHelp: 'Generated from the {{language}} name.',
    search: 'Find a category',
    view_active: 'Active',
    view_archived: 'Archived',
    foldAll: 'Collapse all',
    unfoldAll: 'Expand all',
    toggle: 'Show or hide the sub-categories of {{name}}',
    languages: 'Languages',
    translated: 'Translated into {{language}}',
    untranslated: 'Not translated into {{language}} yet',
    products: 'Products',
    productCount_one: '{{total}} product',
    productCount_other: '{{total}} products',
    edit: 'Edit {{name}}',
    archive: 'Archive {{name}}',
    noMatch: 'No category matches',
    noMatchHelp: 'Try another name, in any language.',
    coverageTitle: 'Translations',
    coverageHelp:
      'The name in the default language is required. Without a translation, the store shows that name.',
    coverageCount: '{{translated}}/{{total}}',
    archiveTip: 'An archived category disappears from the store; its products stay in the catalogue.',
    detailParent: 'Parent category',
    detailRoot: 'Top-level category',
    detailPosition: 'Position',
    detailTranslations: 'Name in each language',
    detailMissing: 'Not translated yet',
    detailNoDescription: 'No description',
  },
  productList: {
    details: 'View',
    active: 'Active',
    adjustRow: 'Operation on {{name}}',
    edit: 'Edit',
    kpi_unitsInDepots_one: '{{units}} unit in {{depots}}',
    kpi_unitsInDepots_other: '{{units}} units in {{depots}}',
    kpi_depots_one: '{{total}} depot',
    kpi_depots_other: '{{total}} depots',
    printLabels: 'Print the labels',
    reference: 'Reference',
    kpi_saleValue: 'Stock value · selling price',
    kpi_costValue: 'At purchase price',
    kpi_ofCatalogue: 'of {{total}} in the catalogue',
    kpi_active: 'Active products',
    kpi_low: 'Low stock',
    kpi_out: 'Out of stock',
    kpi_show: 'See the products',
    product: 'Product',
    variantCount_one: '{{count}} variant',
    variantCount_other: '{{count}} variants',
    allCategories: 'All categories',
    optionValue: 'Option',
    allOptionValues: 'All options',
    findOptionValue: 'Find an option (e.g. Origin)',
    noOptionValueMatch: 'No option matches',
    findCategory: 'Find a category',
    noCategoryMatch: 'No category matches',
    view: 'Show',
    view_all: 'All',
    view_active: 'Active',
    view_draft: 'Inactive',
    view_archived: 'Archived',
    view_low: 'Low stock',
    view_out: 'Out of stock',
    view_sale: 'Sales',
    view_return: 'Back in depot',
    view_defective: 'Defective',
    view_loss: 'Losses',
    operationPeriod: 'Operations period',
    period_last_7_days: 'Last 7 days',
    period_last_30_days: 'Last 30 days',
    period_this_month: 'This month',
    period_last_month: 'Last month',
    period_this_year: 'This year',
    period_custom: 'Custom dates',
    periodFrom: 'From',
    periodTo: 'To',
    sort: 'Sort by',
    sort_created_desc: 'Newest',
    sort_created_asc: 'Oldest',
    sort_updated_desc: 'Recently updated',
    sort_name_asc: 'Name (A → Z)',
    searchShortcut: 'Shortcut: /',
    stock: 'Stock',
    stockUnavailable: 'Stock could not be loaded.',
    selectProduct: 'Select {{name}}',
    selectAll: 'Select the products shown',
    shown: '{{shown}} of {{total}}',
    bulkActions: 'Actions on the selection',
    selected: 'Products selected: {{count}}',
    clearSelection: 'Clear selection',
  },
  productStatus: {
    published: 'Products activated: {{count}}',
    deactivated: 'Products deactivated: {{count}}',
    archived: 'Products deleted: {{count}}',
    restored: 'Products restored: {{count}}',
    failed: '{{name}}: {{reason}}',
  },
  stockValue: {
    title: 'Stock in depots',
    hint: 'At today’s selling price, with its value at purchase price',
    units: 'Units',
    cost: 'Purchase total',
  },
  productStatistics: {
    sectionTitle: 'Statistics and movements',
    netGain: 'Net gain',
    ordered: 'Ordered',
    delivered: 'Delivered',
    manualSales: 'Manual sales',
    carrierReturned: 'Carrier returns',
    receivedBack: 'Back in depot',
    missing: 'Missing',
    missingHint: 'Returned by the carrier but never received in the depot',
    defective: 'Defective',
    lost: 'Losses',
    amount: 'Amount',
    revenue: 'Revenue',
    cost: 'Purchase total',
    margin: 'Margin',
    value: 'Value at cost',
    chartTitle: 'Trend',
    metric_revenue: 'Revenue',
    metric_losses: 'Losses',
    chartLabel: '{{metric}} per period',
    historyTitle: 'Movement history',
  },
  quickStock: {
    title: 'Add stock',
    onHand: 'On hand',
    quickAmounts: 'Quick add',
    addAmount: 'Add {{count}} to {{name}}',
    decrement: 'Remove one unit from {{name}}',
    increment: 'Add one unit to {{name}}',
    total_one: '{{change}} unit',
    total_other: '{{change}} units',
    confirm: 'Add',
    added: 'Stock added: {{change}}',
  },
  adjustStock: {
    operationHint: 'The kind of movement to record.',
    depotHint: 'The depot whose stock changes.',
    linesHint: 'Enter the quantity for each variant concerned.',
    noteHint: 'Shown in the movements journal.',
    title: 'Product operation',
    reason: 'Operation',
    reason_sale: 'Sale',
    reason_return: 'Back in depot',
    reason_defective: 'Defective',
    reason_loss: 'Loss',
    reason_receipt: 'Receipt',
    reason_correction: 'Stock count correction',
    reasonHelp_sale: 'Sale outside an order (shop, live…): removes the units from stock at the sale price.',
    reasonHelp_return: 'Returned units that physically arrived back in the depot: puts them back in stock.',
    reasonHelp_defective: 'Removes damaged units from stock, valued at the purchase price.',
    reasonHelp_loss: 'Removes lost or stolen units from stock, valued at the purchase price.',
    unitPrice: 'Unit sale price',
    unitPriceInvalid: 'Enter the sale price, e.g. 45.000',
    quantity: 'Quantity',
    lines: 'Variants to adjust',
    products_one: '{{count}} product',
    products_other: '{{count}} products',
    notePlaceholder: 'E.g. sale at the 12 October fair, parcel damaged in the depot',
    after: 'After',
    invalid: 'Whole number expected',
    negative: 'Negative stock ({{after}})',
    summary: 'Variants: {{count}} · {{change}}',
    confirm: 'Save adjustment',
    recorded: 'Movements recorded: {{count}}',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    next: 'Next product',
    previous: 'Previous product',
    open: 'Preview the product',
    edit: 'Edit the product',
    adjust: 'Product operation',
    select: 'Select the product',
    search: 'Search',
    help: 'Show this help',
    close: 'Close or clear the selection',
  },
  productImport: {
    openCsv: 'Import (CSV)',
    open: 'Import',
    title: 'Import products',
    drop: 'Drop your CSV file here or click to choose it',
    dropHelp:
      'One row per variant; rows with the same reference form one product. UTF-8, separated by ; or ,',
    template: 'Download the template',
    notCsv: '{{name}} is not a CSV file.',
    unreadable: 'This CSV file cannot be read: save it again as CSV UTF-8 from your spreadsheet.',
    mappingHelp: 'Say what each column holds. Ignored columns are not imported.',
    columns: 'File columns',
    unnamedColumn: 'Column {{position}}',
    importAs: 'Import “{{column}}” as',
    field_ignore: 'Do not import',
    field_reference: 'Reference',
    field_category: 'Category',
    field_sku: 'SKU',
    field_barcode: 'Barcode',
    field_price: 'Selling price',
    field_regularPrice: 'Crossed-out price',
    field_cost: 'Purchase cost',
    field_weightKg: 'Weight (kg)',
    field_stock: 'Initial stock',
    field_option: 'Option (column name)',
    field_name_in: 'Name ({{locale}})',
    field_description_in: 'Description ({{locale}})',
    depot: 'Depot for initial stock',
    chooseDepot: 'Choose a depot',
    problem_reference: 'Map a column to the reference.',
    problem_price: 'Map a column to the selling price.',
    problem_name: 'Map a column to the name in {{locale}}, the shop language.',
    problem_duplicate: 'Two columns are mapped to the same field.',
    problem_options: 'Three option columns at most.',
    otherFile: 'Choose another file',
    check: 'Check the file',
    working_checking: 'Uploading and checking the file…',
    working_applying: 'Creating the products…',
    ready: 'Products ready: {{count}}',
    toFix: 'Rows to fix: {{count}}',
    rowsRead: 'Rows read: {{count}}',
    moreErrors: 'And {{count}} more errors not shown.',
    row: 'Row',
    column: 'Column',
    problem: 'Problem',
    value: 'Value',
    error_required: 'Required value missing',
    error_invalid_decimal: 'Invalid number (e.g. 79.500)',
    error_invalid_integer: 'Whole number expected',
    error_formula: 'Spreadsheet formula refused',
    error_too_long: 'Text too long',
    error_inconsistent_product: 'The rows of this reference do not agree',
    error_mixed_options: 'The variants do not share the same options',
    error_duplicate_variant: 'Duplicate variant',
    error_duplicate_sku: 'SKU used twice in the file',
    error_reference_taken: 'Reference already in the catalog',
    error_sku_taken: 'SKU already in the catalog',
    error_unknown_category: 'Unknown category',
    error_header_mismatch: 'The file header changed',
    error_too_many_rows: 'File too long',
    apply: 'Import the ready products ({{count}})',
    issues: 'Rows to fix',
    issueWhere: 'Row {{row}} · column {{column}}',
    created: 'Products created: {{count}}',
    failed: 'The import could not finish',
    draftsHelp: 'Imported products are inactive: check them, then activate them.',
    failures: 'Products not created',
    failure: '{{reference}} ({{stage}}): {{reason}}',
    stage_product: 'creation',
    stage_stock: 'initial stock',
  },
  quickView: {
    edit: 'Edit',
    noCategory: 'No category',
    noImage: 'No image',
  },
  stockMatrix: {
    threshold: 'Threshold',
    caption: 'Stock by variant and location',
    total: 'Total',
    low: 'Low stock',
    out: 'Out of stock',
    shownTotal: 'Total shown',
    legend_ok: 'Available',
    legend_low: 'Low stock (threshold or below)',
    legend_out: 'Out of stock',
  },
  inventory: {
    depotColumns: 'Depots',
    depotColumnsAll: 'All ({{count}})',
    depotColumnsSome: '{{shown}} of {{total}}',
    depotColumnsStocked: 'With stock',
    levelsTab: 'Stock levels',
    allMovements: 'All',
    variantAndDepot: 'Variant · depot',
    movementsShown_one: '{{shown}} of {{total}} movement',
    movementsShown_other: '{{shown}} of {{total}} movements',
    unitsAvailable: 'Units available',
    levelsSearchLabel: 'Search the stock',
    levelsSearch: 'Product, reference or SKU',
    allDepots: 'All depots',
    restockOnly: 'Low stock only',
    levelsShown_one: '{{shown}} of {{total}} variant shown',
    levelsShown_other: '{{shown}} of {{total}} variants shown',
    depotLow: 'Variants low on stock',
    kpi_available: 'Units available',
    kpi_variants: 'Variants tracked',
    kpi_inProducts_one: 'in {{total}} product',
    kpi_inProducts_other: 'in {{total}} products',
    kpi_low: 'Low stock',
    kpi_lowHint: 'under the alert threshold',
    kpi_out: 'Out of stock',
    kpi_outHint: 'no unit left',
    searchVariant: 'Search for a product',
    search: 'Search',
    searchFirst: 'Search a product by name, reference or SKU',
    context: 'Depots, levels per variant and the movements journal.',
    movementsTab: 'Movements',
    depotsTab: 'Depots',
    variant: 'Variant',
    variantRequired: 'Choose a variant',
    noVariants: 'No variant available',
    depot: 'Depot',
    depotRequired: 'Choose a depot',
    depotName: 'Name',
    depotCode: 'Code',
    defaultDepot: 'Default',
    yes: 'Yes',
    onHand: 'On hand',
    available: 'Available',
    threshold: 'Threshold',
    occurredAt: 'Date',
    kind: 'Kind',
    kind_restock: 'Restock',
    kind_restock_pending: 'Restock pending',
    kind_commit: 'Committed out',
    kind_write_off: 'Write-off',
    kind_adjustment: 'Adjustment',
    kind_import: 'Import',
    quantity: 'Quantity',
    quantityHelp: 'The direction comes from the movement kind',
    quantityRequired: 'The quantity must be a positive whole number',
    note: 'Note',
    state: 'State',
    state_applied: 'Applied',
    state_pending: 'Pending',
    state_voided: 'Voided',
    newMovement: 'New movement',
    movementRecorded: 'Movement recorded',
    loadFailed: 'Could not load',
    noMovements: 'No movements',
    noMovementsHelp: 'Record a first movement to fill the journal.',
    noDepots: 'No depots',
    noDepotsHelp: 'Create a depot to start tracking your stock.',
  },
  customers: {
    identitySection: 'Identity',
    identityHint: 'How to reach this customer.',
    addressSection: 'Delivery address',
    addressHint: 'Prefilled in every new order.',
    notesSection: 'Notes',
    context: 'Your customer book: phone, address and delivery notes.',
    newCustomer: 'New customer',
    editCustomer: 'Edit customer',
    customer: 'Customer record',
    edit: 'Edit',
    name: 'Full name',
    phone: 'Phone',
    phoneHelp: 'Local or international number, e.g. 98 123 456',
    secondPhone: 'Second phone',
    email: 'Email',
    address: 'Address',
    governorate: 'Governorate',
    city: 'City',
    postalCode: 'Postal code',
    notes: 'Notes',
    notesHelp: 'Instructions useful to the courier or the team.',
    createdAt: 'Created',
    searchPhone: 'Search by phone',
    search: 'Search',
    clearSearch: 'Clear',
    noCustomers: 'No customers yet',
    noCustomersHelp: 'Add your first customer; orders will add them too.',
    noMatch: 'No customer found',
    noMatchHelp: 'Check the number or the filters.',
    saved: 'Customer saved',
    savedWithOrders_one: 'Customer saved, and {{count}} order not yet with a carrier updated',
    savedWithOrders_other: 'Customer saved, and {{count}} orders not yet with a carrier updated',
    masked: 'Personal data is masked for your role.',
    phoneTaken: 'A customer already uses this phone number.',
    openExisting: 'Open the record',
    nameRequired: 'The name is required.',
    phoneRequired: 'The phone is required.',
    phoneInvalid: 'This phone number is not valid for the shop country, e.g. 98 123 456.',
    emailInvalid: 'This email address is not valid.',
    fieldInvalid: 'This value is not accepted.',
    shown: 'Customers shown: {{shown}} of {{total}}',
  },
  depots: {
    identitySection: 'Depot',
    identityHint: 'How this depot appears in the app.',
    placeSection: 'Contact and address',
    placeHint: 'Where the depot is and how to reach it.',
    settingsSection: 'Settings',
    defaultSwitch: 'Default depot',
    add: 'New depot',
    newTitle: 'New depot',
    count_one: '{{count}} depot',
    count_other: '{{count}} depots',
    figures: 'Figures of {{name}}',
    saleValue: 'Sale value',
    showMovements: 'See the movements of this depot',
    nameRequired: 'The name is required',
    codeRequired: 'The code is required',
    codeHelp: 'Short and unique, for example TUN or SFX.',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    tooLong: 'This text is too long',
    defaultHelp: 'The depot used by default for orders and stock receipts.',
    defaultKept: 'This is the default depot: choose another one to replace it.',
    editRow: 'Edit {{name}}',
    archiveRow: 'Archive {{name}}',
    archiveTitle: 'Archive {{name}}?',
    archiveMessage:
      'The depot will no longer appear in orders or movements. Its history is kept. A depot that still holds stock cannot be archived.',
    archive: 'Archive',
    archived: '{{name}} is archived',
    created: '{{name}} is created',
    saved: '{{name}} is saved',
  },
  companies: {
    all: 'All companies',
    label: 'Company',
    main: '{{name}} (main company)',
    orderHelp: 'The company decides which products the order can hold and which carrier account ships it.',
  },
  subsidiaries: {
    title: 'Sub-companies',
    context: 'The companies of the shop, used on products, orders, expenses and carrier accounts.',
    archived: 'Archived',
    none: 'No sub-company',
    noneHelp: 'Sub-companies are added through the API.',
    noDetails: 'No contact details',
  },
  team: {
    title: 'Team',
    context: 'The people who work in the shop and what they may do.',
    invite: 'Invite a person',
    you: 'You',
    preset_admin: 'Administrator',
    preset_staff: 'Staff',
    preset_viewer: 'Read only',
    presetHelp_admin: 'Manage everything, including the team and settings.',
    presetHelp_staff: 'Orders, shipping and stock day to day.',
    presetHelp_viewer: 'Look without changing anything.',
    noPreset: 'Access managed elsewhere',
    status_pending: 'Invitation sent',
    status_paused: 'Paused',
    outside: 'Managed outside the portal',
    access: 'Access level',
    extra: 'Extra permissions',
    extraHelp: 'Added to those of the chosen level.',
    group_orders: 'Orders',
    group_catalog: 'Catalog',
    group_stock: 'Stock',
    group_finance: 'Finance',
    group_settings: 'Settings',
    email: 'Email',
    firstName: 'First name',
    lastName: 'Last name',
    subsidiary: 'Sub-company',
    noSubsidiary: 'None',
    inviteTitle: 'Invite a person',
    inviteSubmit: 'Send the invitation',
    inviteHelp: 'The person receives an email to choose a password. The link is valid for 3 days.',
    invited: 'Invitation sent to {{email}}',
    invitedNoEmail: '{{name}} has access, but the email was not sent. Send it again from their details.',
    added: '{{name}} has access to the shop again',
    emailInvalid: 'Enter a valid email address.',
    firstNameRequired: 'Enter a first name.',
    saved: 'Access of {{name}} saved',
    pause: 'Pause',
    resume: 'Let back in',
    paused: '{{name}} is paused',
    resumed: '{{name}} has access again',
    pauseTitle: 'Pause {{name}}?',
    pauseMessage: 'The person is signed out at once and cannot sign in until let back in.',
    remove: 'Remove from the shop',
    removeTitle: 'Remove {{name}}?',
    removeMessage: 'The person loses all access to the shop. Their name stays in the history.',
    removed: '{{name}} was removed from the shop',
    resend: 'Send the invitation again',
    resent: 'Invitation sent again to {{email}}',
    selfNote: 'To change your own access, ask another administrator.',
    perm_orders_write: 'Create and edit orders',
    perm_orders_confirm: 'Confirm orders',
    perm_orders_dispatch: 'Hand orders to carriers and print labels',
    perm_orders_pii_read: 'See customer phones and addresses',
    perm_orders_refund: 'Refund',
    perm_catalog_write: 'Manage products and categories',
    perm_catalog_cost: 'See purchase prices',
    perm_inventory_write: 'Record stock movements',
    perm_finance_read: 'See finance',
    perm_finance_write: 'Manage expenses and remittances',
    perm_carriers_manage: 'Manage carriers',
    perm_settings_manage: 'Manage shop settings',
    perm_users_manage: 'Manage the team',
    perm_audit_read: 'See the change history',
  },
  history: {
    title: 'History',
    context: 'Who changed what in the shop, and when.',
    sectionContext:
      'Every change made in the shop: team, sub-companies, products, categories, depots, order settings and expenses.',
    openPage: 'Open the history',
    kindLegend: 'Kind of change',
    kind_all: 'All',
    kind_people: 'Team',
    kind_subsidiaries: 'Sub-companies',
    kind_products: 'Products',
    kind_categories: 'Categories',
    kind_depots: 'Depots',
    kind_settings: 'Settings',
    kind_finance: 'Expenses',
    kind_exports: 'Exports',
    from: 'From',
    to: 'To',
    byActor: 'Changes by {{name}}',
    byChosenActor: 'Changes by the chosen person',
    everyone: 'Show everyone',
    onlyActor: 'Show only the changes by {{name}}',
    open: 'Open',
    openSubject: 'Open {{subject}}',
    before: 'before',
    after: 'after',
    loading: 'Loading the history',
    shown: '{{count}} of {{total}} changes shown',
    empty: 'No changes yet',
    emptyHint: 'Changes made in the shop will appear here.',
    emptyFiltered: 'No changes match these filters',
    emptyFilteredHint: 'Widen the dates or pick another kind.',
    clearFilters: 'Clear filters',
    actor_person: 'A team member',
    actor_system: 'System',
    actor_support: 'Al-WidadiaShop support',
    actor_unknown: 'Deleted user',
    subject_unnamed: 'unnamed',
    action_order_export_requested: 'exported the orders',
    action_other: 'changed “{{subject}}”',
    action_team_member_invited: 'invited {{subject}} to the team',
    action_team_member_access_changed: 'changed the access of {{subject}}',
    action_team_member_paused: 'paused the access of {{subject}}',
    action_team_member_resumed: 'restored the access of {{subject}}',
    action_team_member_removed: 'removed {{subject}} from the team',
    action_team_member_renamed: 'changed their name to {{subject}}',
    action_user_account_sessions_ended: 'signed out of their other devices',
    field_endedSessions: 'Sessions ended',
    action_subsidiary_created: 'added the sub-company “{{subject}}”',
    action_subsidiary_revised: 'edited the sub-company “{{subject}}”',
    action_subsidiary_archived: 'archived the sub-company “{{subject}}”',
    action_subsidiary_restored: 'restored the sub-company “{{subject}}”',
    action_product_created: 'created the product “{{subject}}”',
    action_product_revised: 'edited the product “{{subject}}”',
    action_product_active: 'published the product “{{subject}}”',
    action_product_draft: 'moved the product “{{subject}}” back to draft',
    action_product_archived: 'archived the product “{{subject}}”',
    action_category_created: 'created the category “{{subject}}”',
    action_category_revised: 'edited the category “{{subject}}”',
    action_category_archived: 'archived the category “{{subject}}”',
    action_location_created: 'added the depot “{{subject}}”',
    action_location_archived: 'archived the depot “{{subject}}”',
    action_order_settings_revised: 'changed the order confirmation delay',
    action_tenant_profile_revised: 'changed the store details of {{subject}}',
    action_tenant_branding_revised: 'changed the store logo and colours',
    field_defaultLocale: 'Default language',
    field_timezone: 'Time zone',
    field_logoMediaId: 'Logo',
    field_primaryColor: 'Primary colour',
    field_secondaryColor: 'Secondary colour',
    field_accentColor: 'Accent colour',
    action_expense_created: 'added the expense “{{subject}}”',
    action_expense_revised: 'edited the expense “{{subject}}”',
    action_expense_archived: 'archived the expense “{{subject}}”',
    action_expense_restored: 'restored the expense “{{subject}}”',
    field_other: 'Other detail',
    field_displayName: 'Name',
    field_preset: 'Access',
    field_subsidiaryId: 'Sub-company',
    field_status: 'Status',
    field_name: 'Name',
    field_taxId: 'Tax ID',
    field_phone: 'Phone',
    field_address: 'Address',
    field_city: 'City',
    field_archived: 'Archived',
    field_reference: 'Reference',
    field_categoryId: 'Category',
    field_price: 'Price',
    field_compareAtPrice: 'Compare-at price',
    field_variantCount: 'Number of variants',
    field_parentId: 'Parent category',
    field_code: 'Code',
    field_isDefault: 'Default depot',
    field_orderConfirmationTtlHours: 'Confirmation delay',
    field_amount: 'Amount',
    field_spentOn: 'Expense date',
    status_draft: 'Inactive',
    status_active: 'Active',
    status_archived: 'Archived',
    status_paused: 'Paused',
    status_removed: 'Removed',
    value_yes: 'Yes',
    value_no: 'No',
    value_none: 'None',
    value_other: 'Deleted or archived',
  },
  labels: {
    firstProducts: 'Only the first {{max}} selected products are prepared for labels.',
    title: 'Labels',
    context: 'Print barcode or QR labels for the chosen variants.',
    printLabels: 'Print labels',
    printFor: 'Print labels for {{name}}',
    format: 'Format',
    format_sheet: 'A4 sheet (24 × 70 × 37 mm)',
    format_roll: 'Roll (50 × 30 mm)',
    symbology: 'Code',
    symbology_code128: 'Barcode',
    symbology_qr: 'QR code',
    showPrice: 'Show the price',
    startAt: 'Start at position',
    startAtHelp: 'From 1 to {{count}}, to reuse a partly used sheet.',
    copiesOf: 'Number of labels for {{name}}',
    summary: 'Labels: {{stickers}} · Pages: {{pages}}',
    print: 'Print',
    tooMany: 'At most {{max}} labels per print. Lower the quantities.',
    unprintable:
      '{{count}} variant(s) will have no printed code: their SKU is too long or has unsupported characters. Choose the QR code.',
    preview: 'Preview of the first page',
    previewEmpty: 'Ask for at least one label to see the preview.',
    nothing: 'No product chosen',
    nothingHint: 'Select products in the list, then choose “Print labels”.',
    chooseProducts: 'Choose products',
    loading: 'Preparing the labels',
    codeUnsupported: 'Code not possible',
    codeTooLong: 'Code too long',
  },
  notifications: {
    filterLegend: 'Show',
    filter_all: 'All',
    filter_unread: 'Unread',
    sectionNew: 'New',
    sectionEarlier: 'Earlier',
    seePrevious: 'See previous notifications',
    seeAll: 'See all notifications',
    pageContext: 'Stock and carrier alerts, and the 8 AM summary.',
    allRead: 'All caught up',
    allReadHelp: 'New alerts will appear here.',
    title: 'Notifications',
    bell: 'Notifications',
    bellUnseen: 'Notifications, {{count}} new',
    announce: 'New notifications: {{count}}',
    markAllRead: 'Mark all as read',
    unread: 'Unread',
    empty: 'Nothing to report',
    emptyHelp: 'Stock and carrier alerts, and the 8 AM summary, will appear here.',
    unknownReason: 'no reason given',
    stock_low_title: 'Low stock: {{product}}',
    stock_low_detail: '{{sku}} at {{depot}}: {{available}} left (threshold {{threshold}}).',
    stock_out_title: 'Out of stock: {{product}}',
    stock_out_detail: '{{sku}} is no longer available at {{depot}}.',
    dispatch_failed_title: 'Parcel refused: {{number}}',
    dispatch_failed_detail: 'The carrier did not register the parcel: {{reason}}',
    carrier_canceled_title: 'Parcel canceled by the carrier: {{number}}',
    carrier_canceled_detail: 'Check the order and send it again if needed.',
    daily_digest_title: 'Summary for {{day}}',
    digest_missing: 'Missing: {{count}}',
    digest_deliveryFailed: 'Delivery failures: {{count}}',
    digest_returnsToReceive: 'Returns to receive: {{count}}',
  },
  orderBulk: {
    cancel: 'Cancel orders',
    cancelHereOnly: 'These orders are not at a carrier yet: they are canceled here only.',
    title_cancel: 'Bulk cancellation',
    cancelTitle: 'Cancel the selected orders: {{count}}',
    cancelMessage:
      'Reserved stock becomes available again. A canceled order cannot be resumed. Orders that cannot be canceled are listed afterwards with the reason.',
    cancelAtCarrier:
      'Parcels already handed to the carrier: {{count}}. They are deleted at the carrier too, as long as it has not picked them up.',
    cancelConfirm: 'Cancel the orders ({{count}})',
    keepOrders: 'Keep the orders',
    handedOver: 'Handed to the carrier: {{count}}',
    awaitingCarrier: 'Waiting for the carrier’s answer: {{count}}',
    retryDispatch: 'Try the hand-over again ({{count}})',
    nothingEligible: 'No selected order allows this action.',
    working: 'Working…',
    notFoundTitle: 'Parcels not found',
    notReadyTitle: 'Not ready yet: the carrier has not registered these parcels',
    separateTitle: 'Labels made by the carrier, to download one by one',
    printPdf: 'Print',
    downloadPdf: 'Download the PDF',
    pdfReady: 'Labels in the PDF: {{count}}',
    skipped: 'Skipped as not in the right state: {{count}}',
    refusedTitle: 'Orders not done',
    refused: 'Not done: {{count}}',
    done: 'Done: {{count}}',
    title_labels: 'Label printing',
    title_dispatch: 'Bulk hand-over to a carrier',
    title_confirm: 'Bulk confirmation',
    printLabels: 'Print labels',
    clear: 'Clear the selection',
    selected: 'Selected orders: {{count}}',
    label: 'Actions on the selection',
    dispatch: 'Hand to a carrier',
    dispatchTitle: 'Hand to a carrier: {{count}} orders',
    dispatchMessage:
      'Each order is handed to the chosen carrier. Those that cannot be are listed afterwards with the reason.',
    confirm: 'Confirm',
  },
  orderExport: {
    title: 'Export orders',
    count_one: '{{total}} order',
    count_other: '{{total}} orders',
    format: 'CSV file, readable in Excel and Google Sheets, in English. Dates follow the store time zone.',
    masked:
      'Customer names and phones will be masked, and addresses left out: your access does not include customer data.',
    prepare: 'Prepare the file',
    preparing: 'Preparing…',
    building: 'Preparing the file. You can keep working.',
    ready_one: 'The file is ready: {{total}} order.',
    ready_other: 'The file is ready: {{total}} orders.',
    failed: 'The file could not be prepared. Try again in a moment.',
    retry: 'Try again',
    download: 'Download the file',
  },
  orders: {
    dispatchFee: 'Delivery fee',
    dispatchFeeOf: 'Delivery fee of {{number}}',
    dispatchFeeInvalid: 'Enter a positive amount with at most 3 decimals.',
    dispatchCollect: 'To collect: {{amount}}',
    carrierFeeMissing: 'No rate set for this carrier: type the fee.',
    selectAll: 'Select every order shown',
    selectRow: 'Select {{number}}',
    labelPrinted: 'Label printed',
    refreshNotFollowed: 'The carrier no longer follows this parcel.',
    refreshQueued: 'Request sent to the carrier; the status will update in a moment.',
    refresh: 'Refresh tracking',
    view_canceled: 'Canceled',
    view_exchanges: 'Exchanges',
    view_returns: 'Returns',
    view_to_receive: 'To receive',
    view_delivered: 'Delivered',
    view_missing: 'Missing',
    view_in_delivery: 'With the carrier',
    view_printed: 'Printed',
    view_to_print: 'To print',
    view_confirmed: 'To dispatch',
    backToAllOrders: 'Back to all orders',
    moreViews: 'More views',
    view_to_confirm: 'To confirm',
    context: 'Confirm, dispatch and follow every parcel until the cash is collected.',
    column_order: 'Order',
    column_items: 'Items',
    column_cod: 'To collect',
    column_carrier: 'Carrier',
    column_barcode: 'Barcode',
    barcodeNone: '—',
    itemCount_one: '{{count}} item',
    itemCount_other: '{{count}} items',
    filters: 'List filters',
    filter_carrier: 'Carrier',
    filter_city: 'City',
    filter_date: 'Date',
    filter_remove: 'Remove the {{name}} filter',
    carrierUnassigned: 'Not assigned',
    searchLabel: 'Find an order',
    searchPlaceholder: 'Order number, tracking or phone',
    sortedNewest: 'Sorted by date, newest first',
    searchScope: 'The 5 best matching orders',
    noSearchMatch: 'No order matches',
    noSearchMatchHelp: 'Check the number, the tracking number or the phone.',
    export: 'Export',
    newOrder: 'New order',
    number: 'Order number',
    customer: 'Customer',
    phone: 'Phone',
    destination: 'Destination',
    total: 'Total',
    status: 'Status',
    createdAt: 'Created',
    status_PENDING: 'Pending',
    status_CONFIRMED: 'Confirmed',
    status_CANCELED: 'Canceled',
    payment_COD_PENDING: 'To collect',
    payment_VOID: 'Void',
    status_DISPATCH_REQUESTED: 'Sending to carrier',
    status_DISPATCHED: 'Dispatched',
    status_IN_TRANSIT: 'In transit',
    status_OUT_FOR_DELIVERY: 'Out for delivery',
    status_DELIVERED: 'Delivered',
    status_DELIVERY_FAILED: 'Delivery failed',
    status_RETURNING: 'Returning',
    status_RETURNED: 'Returned',
    status_RETURN_RECEIVED: 'Return received',
    payment_COD_COLLECTED: 'Collected',
    payment_SETTLED: 'Remitted',
    payment_DISPUTED: 'Disputed',
    dispatch: 'Dispatch',
    dispatching: 'Sending…',
    dispatchTitle: 'Dispatch order {{number}}',
    dispatchMessage:
      'Choose the carrier. The reserved stock leaves the shelf once the carrier registers the parcel.',
    dispatchRequested: 'Order {{number}} sent to the carrier',
    dispatchFailed: 'Last dispatch refused: {{reason}}',
    carrierMessage: 'Carrier message: “{{message}}”',
    carrierStatus: 'Carrier status',
    carrierStatusNone: '—',
    carrierStatusLine: '{{status}} · updated {{when}}',
    noCarrier: 'No carrier is set up for your shop yet.',
    configureCarriers: 'Set up carriers',
    carrier: 'Carrier',
    shipment: 'Shipment',
    carrierReference: 'Carrier delivery note no.',
    trackingNumber: 'Tracking number',
    trackingPending: 'Waiting for the carrier',
    labelPrint: 'Print the label',
    labelPrintFailed: 'Could not open printing. Try again or download the label.',
    labelDownload: 'Download',
    labelPreparing: 'Preparing…',
    labelDownloaded: 'Label downloaded',
    fillSender: 'Fill in the sender',
    view: 'Show',
    view_all: 'All',
    noOrders: 'No orders yet',
    noOrdersHelp: 'Create your first order from a customer.',
    noMatch: 'No orders in this view',
    noMatchHelp: 'Pick another view or create an order.',
    lines: 'Items',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    itemsSubtotal: 'Items subtotal',
    shippingFee: 'Delivery fee',
    totalAmount: 'Total',
    codAmount: 'To collect on delivery',
    delivery: 'Delivery',
    address: 'Address',
    governorate: 'Governorate',
    delegation: 'Delegation',
    postalCode: 'Postal code',
    company: 'Company',
    depot: 'Depot',
    exchange: 'Exchange',
    exchangeHelp: 'The courier brings an item back.',
    note: 'Comment',
    history: 'History',
    historyCreated: 'Created',
    masked: 'Personal data is masked for your role.',
    confirm: 'Confirm',
    reopen: 'Back to pending',
    cancel: 'Cancel order',
    confirmed: 'Order {{number}} confirmed',
    reopened: 'Order {{number}} back to pending',
    canceled: 'Order {{number}} canceled',
    cancelTitle: 'Cancel order {{number}}?',
    cancelMessage: 'Its reserved stock becomes available again. A canceled order cannot be resumed.',
    cancelAtCarrier:
      'The parcel will also be canceled at {{carrier}}. This is no longer possible once the carrier has picked it up.',
    cancelReason: 'Reason',
    keepOrder: 'Keep the order',
    reason_out_of_stock: 'Out of stock',
    reason_unreachable: 'Customer unreachable',
    reason_customer_declined: 'Customer declined',
    reason_fraud_suspected: 'Suspected fraud',
    reason_duplicate: 'Duplicate order',
    reason_other: 'Other',
    reason_unconfirmed_expired: 'Not confirmed in time',
    reason_carrier_canceled: 'Canceled at the carrier',
    edit: 'Edit',
    historyAmended: 'Edited: {{fields}}',
    historyPayment: 'Payment: {{status}}',
    field_shipping: 'address',
    field_lines: 'items',
    field_shippingFee: 'delivery fee',
    field_note: 'comment',
    field_customer: 'customer',
    field_isExchange: 'exchange',
    field_subsidiaryId: 'company',
    source: 'Order source',
    sourceHelp: 'Where the customer came from: feeds the channel table in statistics.',
    source_none: 'Not specified',
    source_facebook: 'Facebook page',
    source_instagram: 'Instagram',
    source_tiktok: 'TikTok',
    source_whatsapp: 'WhatsApp',
    source_phone: 'Phone',
    source_other: 'Other',
    field_source: 'source',
  },
  newOrder: {
    feeAtDispatch: 'The delivery fee is added at dispatch, from the chosen carrier.',
    context: 'Pick the company, the items, then the customer; stock is reserved on creation.',
    customerSection: 'Customer',
    customerSearch: 'Customer',
    customerPlaceholder: 'Pick a customer or type their phone',
    customerByPhoneOnly: 'Type the full phone number to find a customer, or create one.',
    noCustomerFound: 'No customer with this number.',
    createCustomer: 'Create a customer',
    createCustomerNamed: 'Create customer “{{text}}”',
    changeCustomer: 'Change customer',
    deliverySection: 'Delivery',
    selectGovernorate: 'Choose a governorate',
    anyDelegation: 'Not specified',
    itemsSection: 'Items',
    searchProducts: 'Search or scan a product',
    productPlaceholder: 'Name, reference or barcode',
    productHelp: 'Enter adds a scanned product at once.',
    scanAdded: '{{name}} added',
    scanOtherCompany: '{{name}} belongs to another company.',
    scanOutOfStock: '{{name}} is out of stock in this depot',
    scanArchived: 'This product is archived; it cannot be ordered',
    noProducts: 'No product found.',
    add: 'Add',
    added: 'Added',
    available: 'Available: {{quantity}}',
    remove: 'Remove',
    noLines: 'Add at least one item.',
    summarySection: 'Summary',
    create: 'Create order',
    creating: 'Creating…',
    created: 'Order {{number}} created',
    customerRequired: 'Choose a customer.',
    addressRequired: 'The address is required.',
    governorateRequired: 'Choose a governorate.',
    quantityInvalid: 'Enter a whole number from 1 to 10,000.',
    amountInvalid: 'Enter a positive amount with at most 3 decimals.',
    itemsHint: 'Search or scan, then adjust quantities and prices.',
    emptyLinesHint: 'Added items appear here with their quantity and price.',
    customerHint: 'Pick an existing customer or create one in seconds.',
    deliveryHint: 'The address given to the carrier and the depot that packs the parcel.',
    optionsSection: 'Options',
    optionsHint: 'Sales channel, exchange and notes for the courier.',
    sectionComplete: 'Complete',
    readinessTitle: 'Before you create',
    readyItems: 'At least one item',
    readyCustomer: 'A customer chosen',
    readyDelivery: 'An address and a governorate',
    stepDone: 'done',
    stepTodo: 'to do',
    unitsCount: 'Units',
    decreaseQuantity: 'Decrease quantity',
    increaseQuantity: 'Increase quantity',
  },
  errors: {
    DEPENDENCY_UNAVAILABLE: 'A service the shop relies on is not responding. Try again in a few minutes.',
    TENANT_SUBSIDIARY_NAME_TAKEN: 'A sub-company already has this name.',
    TEAM_MEMBER_EXISTS: 'This person is already on the team.',
    TEAM_LAST_ADMIN: 'The shop must keep at least one active administrator.',
    TEAM_SELF_CHANGE: 'You cannot change your own access. Ask another administrator.',
    DASHBOARD_VIEW_LIMIT_REACHED: 'You can keep up to 10 dashboard views. Delete one to save another.',
    DASHBOARD_VIEW_NAME_TAKEN: 'You already have a view with this name. Choose another name.',
    TEAM_GRANT_NOT_ALLOWED: 'Only an administrator can give this access.',
    UNKNOWN: 'Something went wrong. Try again in a moment.',
    SHIPPING_CARRIER_AUTH_FAILED:
      'The carrier refused the account credentials. Check them in Settings, then try again.',
    SHIPPING_CARRIER_NOT_CONFIGURED:
      'This carrier is not set up or is paused. Check it in Settings, then try again.',
    SHIPPING_CANCEL_UNSUPPORTED: 'This carrier cannot cancel parcels online. Call them to cancel the parcel.',
    SHIPPING_CANCEL_REFUSED:
      'The carrier already has the parcel and refused to cancel it. Handle it as a return.',
    SHIPPING_UPDATE_REFUSED:
      'The carrier refused the change: the parcel may already be picked up. Nothing was changed.',
    SHIPPING_UPDATE_UNSUPPORTED:
      'This carrier does not allow changing a parcel already sent. Nothing was changed.',
    SHIPPING_CARRIER_VALIDATION_FAILED:
      'The carrier refused the parcel. Check the customer address and phone, then try again.',
    SHIPPING_DISPATCH_UNCERTAIN:
      'The carrier may already have created the parcel. Check its portal before dispatching again, or there will be a duplicate.',
    ORDERS_CANCEL_IN_PROGRESS: 'The carrier is still cancelling the parcel. The order will update by itself.',
    SHIPPING_CARRIER_UNAVAILABLE: 'The carrier is not responding. Try again in a few minutes.',
    SHIPPING_HANDOVER_DUPLICATE: 'This parcel is already handed over to the carrier.',
    SHIPPING_HANDOVER_REFUSED: 'This parcel or handover cannot be used here.',
    SHIPPING_LABEL_NOT_READY: 'The label is being prepared. Try again in a few seconds.',
    SHIPPING_SENDER_MISSING: 'Fill in the shop sender in Settings to print labels.',
    SHIPPING_UNREACHABLE: 'The shipping service is not responding. Try again in a few minutes.',
    AUTH_ROLE_REQUIRED: 'Your role does not allow this action.',
    CATALOG_CATEGORY_CYCLE: 'A category cannot be its own parent.',
    CATALOG_CATEGORY_NOT_EMPTY: 'This category still holds sub-categories or products.',
    CATALOG_REFERENCE_TAKEN: 'Another product already uses this reference.',
    CATALOG_OPTION_TAKEN: 'This name is already used; pick it from the list.',
    CATALOG_OPTION_RESERVED:
      'The origin is managed by the platform: choose Local or Imported with its country.',
    CATALOG_VARIANT_IN_USE: 'This variant already has stock movements, so it cannot be removed.',
    CATALOG_SLUG_TAKEN: 'That name produces an address already in use. Pick another one.',
    CATALOG_TRANSLATION_MISSING: 'The default language translation is required.',
    CONCURRENCY_CONFLICT: 'Someone changed this while you were editing. Reload and try again.',
    FEATURE_NOT_ENTITLED: 'This feature is not part of your plan.',
    INVENTORY_INITIAL_STOCK_EXISTS: 'This variant already has stock; change it with a movement.',
    INVENTORY_INSUFFICIENT_STOCK: 'Not enough stock in this depot.',
    INVENTORY_LOCATION_ARCHIVED: 'This depot is archived and takes no more movements.',
    INVENTORY_LOCATION_NOT_EMPTY:
      'This depot still holds stock: move or write off its units before archiving it.',
    INVENTORY_DEFAULT_LOCATION_REQUIRED:
      'The shop always keeps a default depot: choose another one before changing this one.',
    MEDIA_TYPE_NOT_ALLOWED: 'This type of file is not accepted here.',
    MEDIA_TOO_LARGE: 'This image is larger than allowed.',
    MEDIA_IMAGE_TOO_LARGE: 'This image has too many pixels: shrink it before uploading.',
    MEDIA_UPLOAD_INCOMPLETE: 'The upload did not finish. Try again.',
    MEDIA_CHECKSUM_MISMATCH: 'The file changed while uploading. Try again.',
    MEDIA_MALWARE_DETECTED: 'This file was blocked by the security scan.',
    MEDIA_PROCESSING_FAILED: 'The image could not be processed. Try again.',
    MEDIA_UPLOAD_EXPIRED: 'The upload expired. Add the image again.',
    MEDIA_NOT_READY: 'An image is still being processed. Wait a moment, then save.',
    RATE_LIMITED: 'Too many requests. Wait a few seconds.',
    RESOURCE_CONFLICT: 'This item already exists.',
    RESOURCE_NOT_FOUND: 'This item could not be found.',
    STATE_TRANSITION_INVALID: 'That status change is not allowed.',
    VALIDATION_FAILED: 'Some fields are invalid. Fix them and try again.',
    CRYPTO_INTEGRITY_FAILED: 'This record cannot be read. Contact support with the time of the error.',
    CRYPTO_UNAVAILABLE: 'Personal data is temporarily unavailable. Try again in a moment.',
    ORDERS_CUSTOMER_PHONE_TAKEN: 'A customer already uses this phone number.',
    IDEMPOTENCY_KEY_REUSED: 'This order changed since it was first sent. Reload the page and try again.',
    ORDERS_NOT_EDITABLE:
      'This order was already handed to the carrier or canceled; it can no longer be edited.',
    ORDERS_EXPORT_TOO_LARGE:
      'Too many orders for one file. Pick a view or add a filter (date, carrier, city), then try again.',
    ORDERS_RETURN_NOT_EXPECTED: 'This parcel is not expected back. Check its status in the orders.',
    ORDERS_LINE_INVALID: 'An item cannot be sold (unknown, archived or repeated). Remove it and try again.',
  },
  statistics: {
    title: 'Statistics',
    context: 'See where you gain and where you lose money, step by step.',
    tab_delivery: 'Delivery',
    tab_confirmation: 'Confirmation',
    tab_products: 'Products',
    tab_orders: 'Orders',
    tab_marketing: 'Marketing',
    scope_delivery: 'Parcels picked up from {{from}} to {{to}}.',
    scope_confirmation: 'Orders received from {{from}} to {{to}}, by how the team handled them.',
    scope_products: 'Units of the orders received from {{from}} to {{to}}.',
    scope_orders: 'Orders received from {{from}} to {{to}}.',
    scope_marketing: 'Orders received from {{from}} to {{to}}, by source.',
    carrierFilter: 'Carrier',
    allCarriers: 'All carriers',
    value: 'Value',
    margin: 'Profit',
    parcel_depot: 'At the carrier’s depot',
    parcel_transit: 'In transit',
    parcel_delivered: 'Delivered',
    parcel_failed: 'Delivery failed',
    parcel_returned: 'Returned',
    delayTitle: 'Delivery time',
    delayHelp: 'Average time from pickup to delivery',
    delayBand_1: 'Next day',
    delayBand_2: '2 days',
    delayBand_3: '3 days',
    delayBand_4: '4 days or more',
    performanceTitle: 'Delivery performance',
    performanceHelp: 'Over {{parcels}} parcels that reached the end of their journey',
    carriersTitle: 'Carrier comparison',
    carrier: 'Carrier',
    parcels: 'Parcels',
    deliveryRate: 'Delivery rate',
    averageDelay: 'Average time',
    owed: 'To collect',
    noParcels: 'No parcel picked up in this period',
    days: '{{value}} d',
    minutes: '{{minutes}} min',
    hoursMinutes: '{{hours}} h {{minutes}}',
    mapTitle: 'Delivery rate by governorate',
    mapLow: 'Under 65%',
    mapHigh: '87% and above',
    mapLabelAll: 'Map of Tunisia by governorate',
    mapLabel: '{{name}}, {{rate}}',
    mapNoParcels: '{{name}}, no parcel',
    mapHint: 'Hover or tab through a governorate to see its figures.',
    mapRank: 'Rank {{rank}} of {{total}} governorates',
    mapSearch: 'Find a governorate',
    bestCarrier: 'Best carrier',
    confirmation_received: 'Orders received',
    confirmation_handled: 'Handled',
    confirmation_confirmed: 'Confirmed',
    confirmation_rejected: 'Rejected',
    confirmation_pending: 'Waiting',
    confirmation_delivered: 'Delivered later',
    confirmation_returned: 'Returned later',
    confirmationRatesTitle: 'Confirmation rate',
    netConfirmation: 'Net (handled orders)',
    globalConfirmation: 'Overall (received orders)',
    responsivenessTitle: 'Team responsiveness',
    confirmationDelayHelp: 'Average time from receipt to confirmation',
    agentsTitle: 'Confirmation rate by agent',
    agentsHelp: 'How confirmed orders end up delivered measures the quality of confirmation.',
    agent: 'Agent',
    formerMember: 'Former member',
    handled: 'Handled',
    confirmationRate: 'Confirmation',
    deliveredAfter: 'Delivered later',
    confirmationDelay: 'Time',
    noDecisions: 'No order handled in this period',
    reasonsTitle: 'Rejection reasons',
    noRejection: 'No order rejected in this period.',
    units_all: 'Units ordered',
    units_pending: 'Waiting',
    units_confirmed: 'Confirmed',
    units_rejected: 'Rejected',
    units_depot: 'At the depot',
    units_transit: 'In transit',
    units_delivered: 'Delivered',
    units_returned: 'Returned',
    orders_all: 'Orders received',
    orders_pending: 'Waiting',
    orders_confirmed: 'Confirmed',
    orders_rejected: 'Rejected',
    orders_depot: 'At the depot',
    orders_transit: 'In transit',
    orders_delivered: 'Delivered',
    orders_returned: 'Returned',
    productsTitle: 'Profitability by product',
    productsHelp: 'Sorted by delivered profit.',
    purchaseTotal: 'Purchase total',
    groupBy: 'Group by',
    byVariant: 'Variant',
    optionTitle: 'Profitability by {{option}}',
    optionHelp: 'Sorted by delivered profit, all variants together.',
    noOptionValue: 'No value',
    product: 'Product',
    unitsDelivered: 'Units delivered',
    revenue: 'Revenue',
    unitsReturned: 'Returns',
    noProducts: 'No unit ordered in this period',
    campaignsLater:
      'Details per campaign and ad arrive with the online shop. The source picked when an order is created already feeds this table.',
    channelsTitle: 'Performance by channel',
    channelsHelp: 'Source recorded on the order.',
    channel: 'Channel',
    orders: 'Orders',
    noOrders: 'No order in this period',
  },
  calculator: {
    title: 'Profitability calculator',
    context: 'Try a product out before you buy ads. Results update as you type.',
    reset: 'Reset',
    useRealRates: 'Use my real rates',
    group_product: 'Your product',
    group_productHelp: 'Start from a catalogue product or type freely.',
    group_advertising: 'Advertising',
    group_advertisingHelp: 'What you pay to receive leads.',
    group_delivery: 'Delivery',
    group_deliveryHelp: 'Your carrier’s rates.',
    group_rates: 'Your rates',
    group_ratesHelp: 'Type your rates or use those of your last 30 days.',
    field_cost: 'Purchase cost',
    field_price: 'Selling price',
    field_priceHelp: 'Delivery included: the amount collected.',
    field_leadCost: 'Cost per lead',
    field_leads: 'Leads received',
    field_deliveryFee: 'Per delivered parcel',
    field_returnFee: 'Per returned parcel',
    field_preparationFee: 'Preparation',
    field_preparationFeeHelp: 'Per confirmed order.',
    field_confirmationRate: 'Confirmation rate',
    field_deliveryRate: 'Delivery rate',
    units: 'units',
    issue_invalid: 'Enter a positive number, for example 3.5.',
    issue_aboveHundred: 'A rate cannot go above 100%.',
    productSearch: 'Start from a product',
    productSearchButton: 'Search',
    productPick: '{{name}} · {{sku}} · {{price}}',
    productNone: 'No active product matches.',
    resultTitle: 'Estimated result',
    profit: 'Estimated net profit',
    loss: 'Estimated loss',
    perDelivered: '{{amount}} per delivered parcel',
    margin: 'margin {{value}}',
    incomplete: 'The result is waiting for your figures',
    incompleteHelp: 'Fix the flagged fields to see the result.',
    funnelTitle: 'From lead to cash',
    funnel_leads: 'Leads',
    funnel_confirmed: 'Confirmed',
    funnel_delivered: 'Delivered',
    funnel_returned: 'Returned',
    splitTitle: 'Where your revenue goes · {{revenue}}',
    split_productCost: 'Product cost',
    split_delivery: 'Delivery',
    split_returns: 'Returns',
    split_preparation: 'Preparation',
    split_advertising: 'Advertising',
    split_profit: 'Profit',
    advertisingPerDelivered: 'Ad cost per delivered parcel',
    breakEven: 'Highest cost per lead',
    tipGain: 'If your delivery rate gains {{points}} points, your profit grows by {{gain}}.',
    tipBreakEven: 'Above {{amount}} per lead, this product loses money.',
    realRatesApplied:
      'Your rates over the last 30 days: confirmation {{confirmation}}%, delivery {{delivery}}%.',
    realRatesMissing: 'Not enough orders over 30 days for one of the rates; the typed value is kept.',
  },
  setup: {
    title: 'Shop setup',
    stepsLabel: 'Setup steps',
    exit: 'Finish later',
    back: 'Back',
    skip: 'Skip this step',
    start: 'Start',
    continue: 'Continue',
    finish: 'Start selling',
    saving: 'Saving…',
    saved: 'Saved automatically',
    finished: 'Your shop is set up.',
    step_welcome: 'Welcome',
    step_welcomeHelp: 'The journey at a glance',
    step_store: 'Your shop',
    step_storeHelp: 'Name, language, time zone',
    step_storeCard: 'Name, language, country and time zone.',
    step_brand: 'Your brand',
    step_brandHelp: 'Logo and colours',
    step_brandCard: 'Logo and colours on your labels.',
    step_delivery: 'Delivery',
    step_deliveryHelp: 'Carrier, sender',
    step_deliveryCard: 'Carrier and shipping address.',
    step_orders: 'Orders',
    step_ordersHelp: 'Confirmation window',
    step_ordersCard: 'How long a phone confirmation may take.',
    welcomeTitle: 'Ready to get your business going?',
    welcomeLead: 'Five short steps. Everything is saved as you go, so you can come back later.',
    video: 'Welcome video',
    storeTitle: 'The essentials',
    storeLead: 'They are used for amounts, dates and the messages sent to your customers.',
    storeName: 'Shop name',
    storeNameHelp: 'Printed on labels and hand-over slips.',
    storeNameInvalid: 'Enter a name of 1 to 200 characters.',
    language: 'Default language',
    languageHelp: 'Each team member can pick their own in their profile.',
    country: 'Country of business',
    countryValue: '{{country}} · {{currency}}',
    countryHelp: 'The country sets the currency; contact support to change it.',
    timezone: 'Time zone',
    timezoneHelp: 'Used for the morning summary and today’s orders.',
    brandTitle: 'Your visual identity',
    brandLead: 'Your logo and colours appear on parcel labels and hand-over slips.',
    logo: 'Logo',
    logoDrop: 'Drop your logo or choose a file',
    logoHelp: 'PNG, JPEG or WebP, 2 MB at most.',
    logoRemove: 'Remove the logo',
    logoUploading: 'Preparing the logo…',
    logoRefused: '“{{name}}” is not a PNG, JPEG or WebP image.',
    logoBusy: 'Wait until the logo is ready before continuing.',
    logoAlt: 'Shop logo',
    colors: 'Colours',
    color_primary: 'Primary',
    color_secondary: 'Secondary',
    color_accent: 'Accent',
    colorReadable: 'White text stays readable.',
    colorLowContrast: 'White text is hard to read on this colour.',
    colorInvalid: 'Expected format: #RRGGBB.',
    colorSample: 'Aa',
    previewTitle: 'Label preview',
    previewRecipient: 'Recipient',
    previewAmount: 'Amount to collect',
    previewSample: 'Amira T. · Sousse',
    deliveryTitle: 'How do you deliver?',
    deliveryLead: 'Connect at least one carrier and enter the sender printed on every parcel.',
    senderTitle: 'Label sender',
    deliveryReadOnly: 'Only someone who manages carriers can change this step.',
    ordersTitle: 'Your orders',
    ordersLead: 'How long may an order wait for phone confirmation before it is cancelled?',
    window: 'Confirmation window',
    windowHours: '{{count}} hours',
    windowHelp_24: 'For products in high demand.',
    windowHelp_48: 'Recommended for most shops.',
    windowHelp_72: 'For customers who are hard to reach.',
    windowHelp_current: 'Your current setting.',
    summaryTitle: 'Summary',
    summaryBranded: 'Logo and colours saved',
    summaryUnbranded: 'Not set yet',
    summaryNoCarrier: 'None yet',
    trackTitle: 'Set up your shop',
    trackSummary_one: '{{count}} step left before you can ship.',
    trackSummary_other: '{{count}} steps left before you can ship.',
    trackSummaryDone: 'Everything is ready. You can review any step.',
    trackProgress: '{{done}} of {{total}}',
    trackToggle: 'Show or hide the steps',
    trackResume: 'Continue setup',
    trackReview: 'Review setup',
  },
  settings: {
    context: 'Everything you need to ship: your carriers, your labels and your order rules.',
  },

  editOrder: {
    title: 'Edit order',
    titleOf: 'Edit {{number}}',
    context: 'Address, items, fee and note; reserved stock follows the new items.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Order {{number}} updated',
    discard: 'Discard changes',
    back: 'Back to the order',
    notEditable: 'This order can no longer be edited',
    notEditableHelp:
      'Pending or confirmed orders can be edited, as well as the address of a parcel not yet picked up by a carrier that accepts changes.',
    atCarrierHelp:
      'The parcel is with {{carrier}} and not yet picked up: only the delivery address and the note can change. The change is sent to {{carrier}}; if it is refused, nothing changes.',
    lockedItems: 'Items (locked once dispatched)',
    sendToCarrier: 'Send to {{carrier}}',
    savedAtCarrier: 'Order {{number}} changed at {{carrier}}',
    masked: 'You cannot see the customer details',
    maskedHelp: 'Ask for access to customer data to edit the address.',
    archivedDepot: 'Archived depot',
  },
  orderSettings: {
    hoursLong: '{{count}} hours',
    rule: 'An order that is not confirmed within this time is canceled automatically and its items go back into stock.',
    title: 'Automatic cancellation',
    context: 'Frees the stock held by orders the customer never confirms.',
    window: 'Confirmation window',
    windowHelp: 'In hours, from 1 to 720 (30 days).',
    windowInvalid: 'Enter a whole number of hours between {{min}} and {{max}}.',
    edit: 'Change the delay',
    saved: 'Confirmation window saved',
  },
  expenses: {
    context: 'The shop expenses, by day and subsidiary. They reduce the net result of the overview.',
    newTitle: 'New expense',
    editTitle: 'Edit expense',
    name: 'Label',
    amount: 'Amount',
    spentOn: 'Date',
    subsidiary: 'Subsidiary',
    wholeShop: 'Whole shop',
    note: 'Note',
    nameRequired: 'Enter a label.',
    amountInvalid: 'Enter a valid amount.',
    dayRequired: 'Choose the day of the expense.',
    archive: 'Archive',
    restore: 'Restore',
    view: 'Show',
    view_active: 'Active',
    view_archived: 'Archived',
    none: 'No expense yet',
    noneHelp: 'Record rent, packaging or advertising to follow the net result.',
    noneArchived: 'No archived expense',
    saved: 'Expense “{{name}}” saved',
    archived: 'Expense “{{name}}” archived',
    restored: 'Expense “{{name}}” restored',
  },
  finance: {
    section_expenses: 'Expenses',
    kpiExpenses: 'Expenses in the period',
    kpiNet: 'Net result',
    sections: 'Finance sections',
    section_overview: 'Overview',
    section_remittances: 'Carrier statements',
    section_disputes: 'Disputes',
    context: 'What each carrier collected, remitted and still owes the shop.',
    carrier: 'Carrier',
    delivered: 'Delivered',
    remitted: 'Remitted',
    carrierFees: 'Delivery fees',
    returnFees: 'Return fees',
    awaiting: 'To remit',
    disputes: 'Disputes',
    kpiAwaiting: 'Collected, not yet remitted',
    kpiRemitted: 'Remitted in the period',
    kpiFees: 'Carrier fees in the period',
    kpiDisputes: 'Open disputes',
    noFigures: 'No figures for this period',
    noFiguresHelp: 'Delivered parcels and imported statements will show here.',
  },
  disputes: {
    context: 'Parcels whose remitted amount differs from the amount to collect.',
    view: 'Show',
    view_disputed: 'Open',
    view_resolved: 'Resolved',
    trackingNumber: 'Tracking number',
    carrier: 'Carrier',
    expected: 'To collect',
    collected: 'Remitted',
    difference: 'Difference',
    since: 'Since',
    status: 'State',
    status_settled: 'Settled',
    status_disputed: 'Disputed',
    status_resolved: 'Resolved',
    none: 'No open dispute',
    noneHelp: 'Amount differences found when importing a statement will show here.',
    noneResolved: 'No resolved dispute',
    resolveTitle: 'Resolve dispute {{trackingNumber}}',
    resolve: 'Resolve',
    expectedWas: 'Amount to collect: {{amount}}',
    collectedAmount: 'Amount finally remitted',
    collectedHelp: 'Leave empty if the amount is still unknown.',
    amountInvalid: 'Enter a valid amount.',
    note: 'Resolution note',
    noteHelp: 'Explain what was agreed with the carrier.',
    noteRequired: 'Add a resolution note.',
    resolved: 'Dispute {{trackingNumber}} resolved',
  },
  remittances: {
    context: 'Import carrier remittance statements: each parcel is settled, disputed or flagged.',
    importTitle: 'Import a statement',
    import: 'Import',
    carrier: 'Carrier',
    noCarrier: 'Set up a carrier account first.',
    sourcePaste: 'Paste or scan',
    sourceCsv: 'CSV file',
    codes: 'Parcel codes',
    codesHelp: 'One parcel per line: scan barcodes or paste code, amount, fee columns.',
    drop: 'Choose or drop a CSV file',
    dropHelp: 'Save the carrier statement as CSV. The file is read in your browser.',
    fileTooLarge: 'This file is larger than 2 MB. Split the statement into several files.',
    nothingRead: 'No parcel code was read. Paste codes or choose a file.',
    preview: 'Parcels read: {{count}}',
    invalidRows: 'Lines skipped because they could not be read: {{rows}}',
    importedAt: 'Imported on',
    source: 'Source',
    source_paste: 'Pasted or scanned',
    source_csv: 'CSV file',
    lines: 'Lines',
    settled: 'Settled',
    disputed: 'Disputed',
    attention: 'To check',
    none: 'No statement imported yet',
    noneHelp: 'Import a carrier remittance statement to settle delivered parcels.',
    detailTitle: 'Imported statement',
    detailTitleOf: 'Statement of {{date}}',
    detailContext: 'The result of each statement line. Disputes are resolved from finance.',
    back: 'Back to statements',
    trackingNumber: 'Tracking number',
    amount: 'Amount',
    fee: 'Fee',
    outcome: 'Result',
    kpi_lineCount: 'Lines',
    kpi_settledCount: 'Settled',
    kpi_disputedCount: 'Disputed',
    kpi_attentionCount: 'To check',
    view: 'Show',
    view_all: 'All',
    view_attention: 'To check',
    view_disputed: 'Disputes',
    view_settled: 'Settled',
    noLines: 'No line in this view',
    outcome_settled: 'Settled',
    outcome_return_fee: 'Return fee',
    outcome_disputed: 'Different amount',
    outcome_duplicate: 'Already settled',
    outcome_not_delivered: 'Not delivered yet',
    outcome_unknown: 'Unknown parcel',
    outcome_wrong_carrier: 'Other carrier',
  },
  parcels: {
    returnedTitle: 'Returned parcels',
    returnedContext_one: '{{count}} parcel returning, returned or received',
    returnedContext_other: '{{count}} parcels returning, returned or received',
    missingTitle: 'Missing parcels',
    missingContext_one: '{{count}} parcel at the carrier and still not delivered after the expected delay',
    missingContext_other: '{{count}} parcels at the carrier and still not delivered after the expected delay',
    searchLabel: 'Search a parcel',
    searchPlaceholder: 'Barcode, order number or phone',
    returnedSince: 'Returned since',
    createdSince: 'Created',
    receive: 'Receive returns',
    returnedEmpty: 'No returned parcel',
    returnedEmptyHelp: 'Parcels the carrier sends back appear here.',
    missingEmpty: 'No missing parcel',
    missingEmptyHelp:
      'A parcel appears here when it is at the carrier and still not delivered after the expected delay.',
    noMatch: 'No parcel matches',
    noMatchHelp: 'Change the search or clear the filters.',
  },
  returns: {
    searchLabel: 'Search returns',
    searchPlaceholder: 'Barcode, order number or phone',
    reported: 'Reported',
    toReceiveNoMatch: 'No parcel matches',
    context:
      'Scan the parcels that came back to the depot (barcode, order number or customer phone), then receive them in one go: intact items go back into stock, damaged items are written off.',
    suggestionsTitle: 'Matching parcels',
    suggestionsCount_one: '{{count}} parcel matches',
    suggestionsCount_other: '{{count}} parcels match',
    noSuggestion: 'No parcel matches',
    searching: 'Searching…',
    inBasket: 'Already in the list',
    notReceivableShort: 'Not receivable yet',
    scanLabel: 'Parcel code',
    scanHelp: 'Barcode, tracking number, order number or customer phone.',
    find: 'Find',
    added: 'Parcel {{number}} added.',
    notFound: '{{code}}: no parcel found.',
    alreadyScanned: 'Parcel {{number}} is already in the list.',
    notReceivable: 'Parcel {{number}} cannot be received (status: {{status}}).',
    chooseTitle: 'Parcels found',
    chooseHelp: 'Several parcels match ({{count}}): pick the one that came back.',
    basketTitle: 'Scanned parcels ({{count}})',
    basketEmpty: 'Scan a parcel or add it from the “To receive” list.',
    parcel: 'Parcel {{number}}',
    articles: 'items: {{count}}',
    notAnnounced: 'Not yet reported as a return by {{carrier}}',
    markDamaged: 'Damaged?',
    damagedCount: 'Damaged: {{count}}',
    remove: 'Remove parcel {{number}}',
    quantity: 'Qty: {{count}}',
    damaged: 'Damaged',
    damagedInvalid: 'Enter a whole number between 0 and {{max}}.',
    restock: 'Back in stock: {{count}}',
    receiveAll: 'Receive {{count}} parcels',
    receiving: 'Receiving…',
    receivedCount: 'Received: {{count}} · refused: {{refused}}',
    confirmTitle: 'Parcels not yet reported as returns: {{count}}',
    confirmMessage:
      '{{numbers}}: the carrier has not reported them as returns yet. Confirm they really came back to the depot.',
    confirmCancel: 'Check first',
    toReceiveTitle: 'To receive ({{count}})',
    toReceiveHelp: 'Parcels the carrier reported as returns that have not been received yet.',
    toReceiveEmpty: 'No parcel waiting to be received.',
    addAll: 'Add all ({{count}})',
    add: 'Add parcel {{number}}',
    addShort: 'Add',
    received: '{{number}} received · back in stock: {{restocked}} · damaged: {{damaged}}',
    sessionTitle: 'Received in this session',
    sessionEmpty: 'No parcel received yet.',
  },
  handover: {
    searchLabel: 'Search handovers',
    searchPlaceholder: 'Parcel tracking or order number',
    noMatch: 'No handover matches',
    noMatchHelp: 'Change the search or clear the filters.',
    context: 'Scan the parcels given to the courier, then print the exit voucher to sign.',
    startTitle: 'New handover',
    noCarrier: 'No carrier is set up. Set one up in Settings.',
    carrier: 'Carrier',
    start: 'Start the handover',
    history: 'Recent handovers',
    openedAt: 'Opened',
    status: 'Status',
    status_open: 'In progress',
    status_closed: 'Closed',
    count: 'Parcels',
    total: 'To collect',
    voucher: 'Exit voucher',
    downloadVoucher: 'Download the exit voucher',
    voucherDownloaded: 'Exit voucher downloaded',
    none: 'No handover yet',
    noneHelp: 'Start a handover to scan the parcels given to the courier.',
    scanLabel: 'Tracking or order number',
    scanHelp:
      'Scan the label or type a number: the ready parcels list filters as you type, Enter adds the parcel.',
    add: 'Add',
    addParcel: 'Add parcel {{trackingNumber}}',
    readyTitle: 'Parcels ready to hand over',
    readyNone: 'No parcel is waiting for this carrier.',
    readyNoMatch: 'No ready parcel matches “{{search}}”.',
    accepted: '{{trackingNumber}} handed over to the carrier',
    back: 'All handovers',
    countValue: '{{count}} parcels',
    trackingNumber: 'Tracking number',
    order: 'Order',
    recipient: 'Recipient',
    phone: 'Phone',
    place: 'Destination',
    amount: 'Amount',
    empty: 'No parcel scanned',
    emptyHelp: 'Scan the first parcel to begin.',
    close: 'Close and print the voucher',
    closeTitle: 'Close the handover?',
    closeMessage:
      '{{count}} parcels will be listed on the {{carrier}} exit voucher. The handover cannot be changed afterwards.',
    closeConfirm: 'Close the handover',
    refused_closed: 'This handover is closed.',
    refused_wrong_carrier: 'This parcel belongs to another carrier.',
    refused_not_ready:
      'This parcel is not waiting for pickup (already picked up, cancelled or not registered yet).',
    refused_empty: 'Scan at least one parcel before closing.',
    refused_currency: 'This parcel is not in the handover currency.',
  },
  sender: {
    taxIdLine: 'Tax id: {{value}}',
    taxIdMissing: 'No tax id (optional)',
    previewLabel: 'Label preview',
    previewCaption: 'Sender',
    sectionContext: 'The “Sender” block printed on every parcel label (Mylerz, Al Amana, test carrier).',
    sectionTitle: 'Labels',
    title: 'Sender',
    help: 'This appears in the “Sender” block of every label.',
    name: 'Shop name',
    phone: 'Phone',
    address: 'Address',
    taxId: 'Tax ID or national ID',
    taxIdHelp: 'Optional.',
    fieldRequired: 'This field is required.',
    saved: 'Sender saved',
    fill: 'Fill in the sender',
    edit: 'Edit',
    none: 'No sender yet',
    noneHelp: 'Fill it in to print labels.',
  },
  carriers: {
    customerFee: 'Delivery fee charged to the customer',
    customerFeeHelp:
      'Fixed price per parcel charged to the customer: it sets the delivery fee of the orders that choose this carrier.',
    customerFeeInvalid: 'Enter a positive amount with at most 3 decimals.',
    customerFeeShown: 'Customer fee: {{fee}}',
    connectedCount: 'Connected: {{count}} of {{total}}',
    secretHint: 'Encrypted credentials, ending in',
    changeCredentials: 'Change credentials',
    connect: 'Connect',
    state_soonHelp: 'This integration is on its way.',
    state_soon: 'Coming soon',
    state_notConnectedHelp: 'Enter the credentials the carrier gave you.',
    state_notConnected: 'Not connected',
    state_pausedHelp: 'Shipping is suspended. The credentials are kept.',
    state_paused: 'Paused',
    state_connectedHelp: 'Your parcels can leave with this carrier.',
    state_connected: 'Connected',
    title: 'Carriers',
    context: 'The delivery companies that pick up your parcels. Connect the ones you have a contract with.',
    carrier: 'Carrier',
    none: 'No carrier available',
    accountTitle: '{{carrier}} account',
    accountHelpNew: 'Enter the credentials the carrier gave you.',
    accountHelpReplace: 'Current credentials are never shown. Enter them again to replace them.',
    fieldRequired: 'This field is required.',
    enabled: 'Use this account for dispatch',
    enabledHelp: 'Turn it off to pause dispatch without losing the credentials.',
    saved: '{{carrier}} account saved',
    field_apiKey: 'API key',
    field_username: 'Username',
    field_password: 'Password',
    field_token: 'Token',
    field_userId: 'Customer ID',
    field_warehouseName: 'Pickup warehouse',
    field_parcelType: 'Parcel type',
  },
  table: { previous: 'Previous', next: 'Next', range: '{{from}}–{{to}} of {{total}}' },
};
